import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, Loader2, Plus, Search } from 'lucide-react';
import { travelCariHref, TRAVEL_PARTNER_CARI_CODES } from '@polatkan/api/travel-cari';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { NewCariDialog } from '@/components/travel/cari/new-cari-dialog';
import { CariCreateModals } from '@/components/travel/cari/cari-create-modals';
import { cariTypeLabel } from '@/components/travel/cari/cari-type-display';
import { usePermission } from '@/components/guards/permission-guard';
import { trpc } from '@/lib/trpc';

export function TravelCariKartlarPage() {
  const { t, i18n } = useTranslation('travelCari');
  const { t: partners } = useTranslation('partners');
  const { t: common } = useTranslation('common');
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const query = params.get('q') ?? '';
  const [search, setSearch] = useState(query);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [createType, setCreateType] = useState<string | null>(null);
  const createCustomer = usePermission('CUSTOMERS', 'CREATE', 'TRAVEL');
  const createPartner = usePermission('PARTNERS', 'CREATE', 'TRAVEL');
  const viewCustomer = usePermission('CUSTOMERS', 'VIEW', 'TRAVEL');
  const viewPartner = usePermission('PARTNERS', 'VIEW', 'TRAVEL');
  const viewAccounting = usePermission('ACCOUNTING', 'VIEW', 'TRAVEL');
  const rawType = params.get('type') ?? '';
  const typeCode = rawType === 'CUSTOMER' ? 'M' : (TRAVEL_PARTNER_CARI_CODES[rawType] ?? rawType);
  const source =
    params.get('source') === 'customer'
      ? 'customer'
      : params.get('source') === 'partner'
        ? 'partner'
        : undefined;
  const pageNumber = Number(params.get('page') ?? '1');
  const page = Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1;
  const sortBy = params.get('sort') === 'code' ? 'code' : 'name';
  const sortDir = params.get('dir') === 'desc' ? 'desc' : 'asc';
  const { data: types } = trpc.travelCariType.list.useQuery({ includeInactive: true });
  const { data: counts } = trpc.travelCari.cardCounts.useQuery();
  const { data, isLoading, error, refetch } = trpc.travelCari.cards.useQuery({
    search: query || undefined,
    cariType: typeCode || undefined,
    source,
    page,
    pageSize: 30,
    sortBy,
    sortDir,
  });
  const utils = trpc.useUtils();
  const change = (key: string, value: string) =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  useEffect(() => {
    setSearch(query);
  }, [query]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search.trim() !== query)
        setParams(
          (current) => {
            const next = new URLSearchParams(current);
            if (search.trim()) next.set('q', search.trim());
            else next.delete('q');
            next.delete('page');
            return next;
          },
          { replace: true }
        );
    }, 300);
    return () => clearTimeout(timer);
  }, [search, query, setParams]);
  const label = (code: string) => {
    const type = types?.find((row) => row.code === code);
    return type ? cariTypeLabel(type, i18n.language) : code;
  };
  const sort = (key: 'name' | 'code') =>
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set('sort', key);
      next.set('dir', sortBy === key && sortDir === 'asc' ? 'desc' : 'asc');
      next.delete('page');
      return next;
    });
  const items = data?.items ?? [];
  const pag = data?.pagination;
  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('directory.title')}</h1>
          <p className="text-muted-foreground mt-1 text-sm">{t('directory.subtitle')}</p>
        </div>
        {(createCustomer || createPartner) && (
          <Button onClick={() => setChooserOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('account.new')}
          </Button>
        )}
      </header>
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[220px] flex-1 space-y-1.5">
          <Label htmlFor="cari-search">{common('search')}</Label>
          <div className="relative">
            <Search className="text-muted-foreground absolute left-2.5 top-2 h-4 w-4" />
            <Input
              id="cari-search"
              className="pl-9"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('directory.search')}
            />
          </div>
        </div>
        <div className="w-full space-y-1.5 sm:w-72">
          <Label>{t('directory.type')}</Label>
          <Combobox
            ariaLabel={t('directory.type')}
            value={typeCode || '__all__'}
            onValueChange={(value) => change('type', value === '__all__' ? '' : value)}
            options={[
              { value: '__all__', label: t('directory.allTypes') },
              ...(types ?? [])
                .filter(
                  (type) =>
                    viewAccounting || (type.source === 'customer' ? viewCustomer : viewPartner)
                )
                .map((type) => ({
                  value: type.code,
                  label: `${type.code} · ${label(type.code)} (${counts?.byType[type.code] ?? 0})`,
                  keywords: [type.code, type.name, type.nameEn ?? ''],
                })),
            ]}
          />
        </div>
        {source && (
          <Button variant="outline" onClick={() => change('source', '')}>
            {t('directory.showAll')}
          </Button>
        )}
        {(typeCode || query) && (
          <Button
            variant="ghost"
            onClick={() => {
              setSearch('');
              setParams((current) => {
                const next = new URLSearchParams(current);
                next.delete('q');
                next.delete('type');
                next.delete('page');
                return next;
              });
            }}
          >
            {t('directory.clear')}
          </Button>
        )}
      </div>
      {error ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 border p-5">
          <p>{t('directory.loadFailed')}</p>
          <Button
            variant="outline"
            onClick={() => {
              void refetch();
            }}
          >
            {common('retry', 'Retry')}
          </Button>
        </div>
      ) : (
        <div className="bg-card overflow-x-auto border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b text-left">
              <tr>
                <th
                  className="px-4 py-3"
                  aria-sort={
                    sortBy === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                >
                  <button className="flex items-center gap-2" onClick={() => sort('name')}>
                    {t('directory.name')}
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th
                  className="px-4 py-3"
                  aria-sort={
                    sortBy === 'code' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                  }
                >
                  <button className="flex items-center gap-2" onClick={() => sort('code')}>
                    {t('directory.code')}
                    <ArrowUpDown className="h-3.5 w-3.5" />
                  </button>
                </th>
                <th className="px-4 py-3">{t('directory.type')}</th>
                <th className="px-4 py-3">{t('account.contact')}</th>
                <th className="px-4 py-3">{t('directory.status')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="p-10 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                    <span className="sr-only">{common('loading')}</span>
                  </td>
                </tr>
              ) : items.length ? (
                items.map((item) => (
                  <tr
                    key={`${item.source}:${item.id}`}
                    className="hover:bg-muted/30 border-b last:border-0"
                  >
                    <td className="min-w-[240px] px-4 py-3">
                      <Link
                        className="font-medium hover:underline"
                        to={travelCariHref(item.source, item.id)}
                        state={{ returnTo: `/travel/cari${location.search}` }}
                      >
                        {item.name}
                      </Link>
                      {item.secondary && (
                        <p className="text-muted-foreground mt-0.5 text-xs">{item.secondary}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{item.legacyCariCode || '—'}</td>
                    <td className="min-w-[150px] px-4 py-3">
                      {item.cariType} · {label(item.cariType)}
                    </td>
                    <td className="text-muted-foreground min-w-[190px] px-4 py-3">
                      {item.contactName && <p>{item.contactName}</p>}
                      {item.email && <p>{item.email}</p>}
                      {item.phone && <p>{item.phone}</p>}
                      {!item.contactName && !item.email && !item.phone && '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs">
                      {item.source === 'partner'
                        ? partners(`statusLabels.${item.status}`, item.status)
                        : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="space-y-2 p-10 text-center">
                    <p className="font-medium">{t('directory.empty')}</p>
                    <p className="text-muted-foreground">
                      {t(query || typeCode ? 'directory.emptySearch' : 'directory.emptyNew')}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {pag && pag.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <p className="text-muted-foreground">
            {t('directory.results', {
              from: (page - 1) * pag.pageSize + 1,
              to: Math.min(page * pag.pageSize, pag.total),
              count: pag.total,
            })}
          </p>
          {pag.totalPages > 1 && (
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => change('page', String(page - 1))}
              >
                {common('previous', 'Previous')}
              </Button>
              <span>
                {page} / {pag.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page >= pag.totalPages}
                onClick={() => change('page', String(page + 1))}
              >
                {common('next', 'Next')}
              </Button>
            </div>
          )}
        </div>
      )}
      <NewCariDialog open={chooserOpen} onOpenChange={setChooserOpen} onSelect={setCreateType} />
      <CariCreateModals
        createType={createType}
        onClose={() => {
          setCreateType(null);
          void utils.travelCari.invalidate();
          void utils.travelCariType.invalidate();
        }}
      />
    </div>
  );
}
